import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, useToast } from '@musa.tech/design-system';
import { WizardLayout } from '@/components/collects/sob-demanda/wizard';
import { useAuthContext } from '@/components/AuthProvider';
import { useBucketContracts, useCreateBucketMovements } from '@/hooks/useAgreementsOnDemand';
import { AgreementsApiError, type BucketContract } from '@/integrations/agreements/client';
import { dayInOperationTimezone } from '@/utils/onDemandRequestRules';
import { BUCKET_TYPE_LABELS, bucketBackdateFrom, bucketMinAllowedDate } from '@/utils/bucketRequestRules';
import { useCanBackdateBucketRequests } from '@/hooks/useCanBackdateBucketRequests';
import { useNow } from '@/hooks/useNow';
import { applyWeeklyFloor, describeWeeklyFloor, weeklyFirstAllowedDay } from '@/utils/weeklyDateFloor';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toAgreementsErrorCode } from '@/utils/analyticsErrorCode';
import { onDemandButtonLabel } from './onDemandAnalytics';
import {
  BUCKET_CONTRACTS_PAGE_SIZE,
  BucketContractSelectionStep,
} from './BucketContractSelectionStep';
import { BucketRequestDetailsStep } from './BucketRequestDetailsStep';
import {
  initialBucketRequestForm,
  readBucketBatchResult,
  toBucketRequestItems,
  validateBucketRequestForm,
  type BucketRequestFormState,
} from './bucketRequestForm';
import { BUCKET_FIELD_TYPES, BUCKET_QUANTITY_FIELDS, describeBucketQuantities } from './bucketQuantities';

const STEPS = [
  { id: 'contrato', label: 'Contrato' },
  { id: 'detalhes', label: 'Detalhes' },
];

export const BUCKET_LIST_PATH = '/coletas?tab=sobdemanda&servico=cacamba';

/**
 * Movimentação de equipamento: pedido de colocação ou retirada de caçamba — o gerador não pede
 * troca; para trocar, pede uma retirada e uma colocação. Nasce pendente do transportador, que
 * aceita ou recusa no portal dele — o mesmo fluxo de quando a Musa agenda pelo Hub.
 *
 * A operação Musa que agenda caçamba no Hub pede também com data passada, para formalizar o que
 * foi combinado por fora da plataforma; o caminho depois é o mesmo.
 */
export const BucketRequestWizard: React.FC<{ serviceToggle: React.ReactNode }> = ({ serviceToggle }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuthContext();
  const isInternal = !!profile?.is_internal;
  const canBackdate = useCanBackdateBucketRequests();
  const { trackButtonClick, trackError } = useAnalytics();

  const [step, setStep] = useState(0);
  const [generatorId, setGeneratorId] = useState('');
  const [page, setPage] = useState(1);
  const [contract, setContract] = useState<BucketContract | null>(null);
  const [form, setForm] = useState<BucketRequestFormState>(() => initialBucketRequestForm());
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [failures, setFailures] = useState<Array<{ label: string; error: string }>>([]);
  const [partialSucceeded, setPartialSucceeded] = useState(0);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // o perfil chega depois do primeiro render; só preenche o que a pessoa ainda não digitou.
  // Depende dos campos, não do objeto: um perfil recriado a cada render entraria em laço.
  const profileName = profile?.user_name ?? '';
  const profilePhone = profile?.phone_number ?? '';
  useEffect(() => {
    if (isInternal || (!profileName && !profilePhone)) return;
    setForm((current) => ({
      ...current,
      contactName: current.contactName || profileName,
      contactPhone: current.contactPhone || profilePhone,
    }));
  }, [profileName, profilePhone, isInternal]);

  const contracts = useBucketContracts(
    { generatorId: generatorId ? Number(generatorId) : undefined, page, pageSize: BUCKET_CONTRACTS_PAGE_SIZE },
    { enabled: !isInternal || !!generatorId },
  );
  const createMovements = useCreateBucketMovements();

  const today = useMemo(() => dayInOperationTimezone(new Date()), []);
  // operação Musa: data passada até a janela de registro, sem antecedência — como agenda no Hub
  const backdateFrom = useMemo(() => (canBackdate ? bucketBackdateFrom() : null), [canBackdate]);
  // piso semanal: depois do corte de sexta, ou no fim de semana, só a partir de terça. A operação
  // Musa que agenda como no Hub não tem o piso, como não tem a antecedência
  const now = useNow();
  const weeklyFloor = useMemo(
    () => (contract && !backdateFrom ? weeklyFirstAllowedDay(contract.generator.name, now) : null),
    [contract, backdateFrom, now],
  );
  // a antecedência da regra e o piso são conferidos de novo pelo serviço; aqui só travam o calendário
  const minDate = useMemo(
    () => backdateFrom ?? (contract ? applyWeeklyFloor(bucketMinAllowedDate(contract.rule, now), weeklyFloor) : today),
    [backdateFrom, contract, now, weeklyFloor, today],
  );
  const weeklyNote = contract && weeklyFloor ? describeWeeklyFloor(contract.generator.name, weeklyFloor) : null;
  const rules = useMemo(
    () => ({ requiresNumber: !!contract?.requiresNumber, requiresPhoto: !canBackdate }),
    [contract, canBackdate],
  );
  const errors = useMemo(
    () => (contract
      ? validateBucketRequestForm(form, {
        today,
        minDate,
        maxDate: contract.endsAt,
        backdateFrom,
        // o aviso da antecedência erraria o motivo quando é o piso que manda
        minDateMessage: weeklyFloor === minDate && weeklyNote ? weeklyNote : undefined,
      }, rules)
      : {}),
    [contract, form, today, minDate, backdateFrom, rules, weeklyFloor, weeklyNote],
  );

  const handleSubmit = async () => {
    if (!contract) return;

    setShowErrors(true);
    setSubmitError(null);
    setFailures([]);
    setPartialSucceeded(0);
    if (Object.keys(errors).length) return;

    const items = toBucketRequestItems(form);

    try {
      const result = await createMovements.mutateAsync({
        contractId: contract.contractId,
        inputs: items.map((item) => item.input),
      });
      const outcome = readBucketBatchResult(form, items, result);

      // avisos de limite e de data nunca bloqueiam, mas o gerador precisa saber deles
      for (const warning of outcome.warnings) {
        toast({ variant: 'warning', title: 'Atenção', description: warning.message });
      }

      if (outcome.failures.length) {
        // parte do lote entrou: conta como conclusão parcial, não como sucesso do funil
        if (outcome.succeeded > 0) {
          trackButtonClick({ buttonLabel: onDemandButtonLabel('bucket', 'criar_solicitacao_parcial') });
        }
        // o que entrou já existe: o formulário passa a pedir só o resto
        setForm(outcome.remaining);
        setFailures(outcome.failures);
        setPartialSucceeded(outcome.succeeded);
        return;
      }

      trackButtonClick({ buttonLabel: onDemandButtonLabel('bucket', 'criar_solicitacao_sucesso') });
      toast({
        variant: 'success',
        title: describeCreated(form, outcome.succeeded),
        description: 'O transportador vai aceitar ou recusar. Acompanhe o status na listagem.',
      });

      navigate(BUCKET_LIST_PATH);
    } catch (error) {
      trackError({ errorType: 'api_error', errorCode: toAgreementsErrorCode(error) });
      if (error instanceof AgreementsApiError && (error.isBusinessRule || error.isForbidden || error.status === 404 || error.status === 422)) {
        setSubmitError(error.message);
        return;
      }
      toast({
        variant: 'error',
        title: 'O pedido não foi criado',
        description: 'O serviço de coletas está indisponível no momento. Seus dados continuam aqui — tente de novo em instantes.',
      });
    }
  };

  const isSubmitting = createMovements.isPending;

  // sair pela primeira etapa é o abandono do formulário, como no fluxo antigo; o X não conta
  const handleCancel = () => {
    trackButtonClick({ buttonLabel: onDemandButtonLabel('bucket', 'voltar_formulario') });
    navigate(BUCKET_LIST_PATH);
  };

  const handleContinue = () => {
    trackButtonClick({ buttonLabel: onDemandButtonLabel('bucket', 'form_next_step'), form_step: 'contrato' });
    setStep(1);
  };

  const footer = step === 0 ? (
    <>
      <Button variant="outline" onClick={handleCancel}>
        <ChevronLeft className="mr-2 h-4 w-4" aria-hidden="true" />
        Cancelar
      </Button>
      <Button disabled={!contract} onClick={handleContinue} data-testid="bucket-next">
        Continuar com o contrato
        <ChevronRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={() => setStep(0)} disabled={isSubmitting}>
        <ChevronLeft className="mr-2 h-4 w-4" aria-hidden="true" />
        Voltar
      </Button>
      <Button onClick={handleSubmit} disabled={isSubmitting || isUploadingPhoto} data-testid="bucket-submit">
        {isSubmitting ? 'Enviando pedido...' : 'Pedir movimentação'}
      </Button>
    </>
  );

  return (
    <WizardLayout steps={STEPS} currentIndex={step} onClose={() => navigate(BUCKET_LIST_PATH)} footer={footer}>
      {step === 0 || !contract ? (
        <BucketContractSelectionStep
          contracts={contracts.data?.items ?? []}
          total={contracts.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          isLoading={contracts.isLoading}
          isError={contracts.isError}
          generatorId={generatorId}
          onGeneratorChange={(value) => {
            setGeneratorId(value);
            setPage(1);
            setContract(null);
          }}
          requiresGenerator={isInternal}
          selectedContractId={contract?.contractId ?? null}
          onSelect={(selected) => {
            setContract(selected);
            setSubmitError(null);
            setFailures([]);
            setPartialSucceeded(0);
          }}
          headerSlot={serviceToggle}
        />
      ) : (
        <BucketRequestDetailsStep
          contract={contract}
          form={form}
          onChange={setForm}
          minDate={minDate}
          weeklyNote={weeklyNote}
          backdate={!!backdateFrom}
          errors={showErrors ? errors : {}}
          submitError={submitError}
          failures={failures}
          partialSucceeded={partialSucceeded}
          onPhotoUploadingChange={setIsUploadingPhoto}
        />
      )}
    </WizardLayout>
  );
};

/** "Pedido de colocação criado", "3 pedidos de retirada criados", "3 pedidos criados: 2 retiradas e 1 colocação". */
function describeCreated(form: BucketRequestFormState, count: number): string {
  const fields = BUCKET_QUANTITY_FIELDS.filter((field) => form[field] > 0);

  if (fields.length > 1) {
    return `${count} pedidos criados: ${describeBucketQuantities(form)}`;
  }

  const what = BUCKET_TYPE_LABELS[BUCKET_FIELD_TYPES[fields[0]]].toLowerCase();
  return count === 1 ? `Pedido de ${what} criado` : `${count} pedidos de ${what} criados`;
}
